import axios from "axios";
import Hasher from "node-object-hash";
import apiUrlConfig from "../../environmentSettings";
import { indicatorRequestLimiter } from "../limiter";
import { perfLog } from "../../utility/perfLog";

export const getDashboardContainers = organizationId => {
	return axios.get("/api/dashboardContainers", {
		params: {
			filter: { where: { organizationId: organizationId } }
		}
	});
};
export const getKpiDashboardContainers = () => {

	return axios.get("/api/dashboardContainers", {
		params: {
			filter: { where: { organizationId:apiUrlConfig.apiConfig.kpiOrgId } }
		}
	});
};

export const getBuilderSettings = organizationId => {
	return axios.get("/api/builderSettings/findOne", {
		params: {
			filter: { where: { organizationId: organizationId } }
		}
	});
};

export const updateBuilderSettings = (id, params) => {
	return axios.put("/api/builderSettings/" + id, params);
};

// Custom Icons API
export const getCustomIcons = (organizationId) => {
	return axios.get("/api/builderSettings/findOne", {
		params: {
			filter: { 
				where: { organizationId: organizationId },
				fields: { customIcons: true, id: true }
			}
		}
	}).then(response => {
		return response.data?.customIcons || [];
	}).catch(error => {
		console.error("Error fetching custom icons:", error);
		return [];
	});
};

export const saveCustomIcons = (organizationId, icons) => {
	// First get the builderSettings ID for this organization
	return axios.get("/api/builderSettings/findOne", {
		params: {
			filter: { 
				where: { organizationId: organizationId },
				fields: { id: true }
			}
		}
	}).then(response => {
		const settingsId = response.data?.id;
		if (settingsId) {
			// Update the customIcons field
			return axios.patch("/api/builderSettings/" + settingsId, {
				customIcons: icons
			});
		} else {
			// Create new builderSettings if doesn't exist
			return axios.post("/api/builderSettings", {
				organizationId: organizationId,
				customIcons: icons
			});
		}
	});
};


export const addDashboardContainer = params => {
	return axios.post("/api/dashboardContainers", params);
};

export const addDashboardAndAddToDashboardContainer = params => {
	return axios.post("/api/dashboards/createAndAddToDashboardContainer", params);
};

export const addWidgetToDashboard = params => {
	const enrichedParams = {
		...params,
		placementData: {
			...(params.placementData || {}),
			useProvidedCoords: true,
		},
	};
	return axios.post("/api/widgets/createAndAddToDashboard", enrichedParams);
};

export const deleteDashboard = dashboardId => {
	return axios.delete("/api/dashboards/" + dashboardId);
};

export const deleteDashboardContainer = (dashboardContainerId, params) => {
	return axios.delete("/api/dashboardContainers/" + dashboardContainerId);
};

export const updateDashboardContainer = (dashboardContainerId, params) => {
	return axios
		.put("/api/dashboardContainers/" + dashboardContainerId, params)
		.then(response => response.data);
};

export const getDashboardHeirarchy = organizationId => {
	return axios.post("/api/dashboardContainers/getDashboards", {
		organizationId: parseInt(organizationId)
	});
};

export const getDashboardWidgets = dashboardId => {
	const __start = Date.now();
	return axios
		.get(`/api/widgets/`, {
			params: {
				filter: {
					where: {
						dashboardId: {
							inq: [dashboardId]
						}
					}
				}
			}
		})
		.then(res => {
			const __networkMs = Date.now() - __start;
			let widgets = res.data;
			let response = {
				previewWidgets: [],
				editorWidgets: []
			};
			widgets.forEach(widget => {
				// For published widgets (previewWidgets), use ONLY root-level data, NOT drafted
				// This ensures preview shows only published changes, not drafted changes
				const publishedConfig = widget.config || {};
				
				let widgetObj = {
					id: widget.id,
					refWidgetId: widget.refWidgetId,
					configuration: {
						data: widget.data,
						props: widget.props,
						config: { 
							...publishedConfig, 
							name: widget.name, 
							chartLibrary: widget.chartLibrary,
							// Include chartType in config for WidgetViewRenderer
							chartType: widget.chartType || publishedConfig?.chartType
						},
						facts: widget.facts
					},
					position: widget.position,
					placement: widget.placement || "components",
					created: widget.created,
					modified: widget.modified,
					modifierId: widget.modifierId,
					creatorId: widget.creatorId,
					organizationId: widget.organizationId,
					isExternal: widget.isExternal,
					// For published widgets, use root-level editorSource, not drafted
					editorSource: widget.editorSource,
					// Preserve chartLibrary from widget-level (MongoDB)
					chartLibrary: widget.chartLibrary,
					chartType: widget.chartType
				};
				// For published widgets, only use root-level position.editorSource
				if (widget.position && widget.position.editorSource) {
					widgetObj.position = {
						...widgetObj.position,
						editorSource: widget.position.editorSource
					};
				}
				if (widget.placement && widget.position) {
					response.previewWidgets.push(widgetObj);
				}
				if (widget.drafted) {
					if (widget.drafted.active && widget.drafted.placement && widget.drafted.position) {
						const draftedChartType = widget.drafted.chartType || widget.chartType || 
							widget.drafted.config?.chartType || widget.config?.chartType;
						widgetObj = {
							id: widget.id,
							refWidgetId: widget.refWidgetId,
							configuration: {
								data: widget.drafted.data || widget.data || [],
								props: widget.drafted.props || widget.props || {},
								config: {
									...(widget.drafted.config || widget.config),
									name: widget.drafted.name || widget.name,
									chartLibrary: widget.drafted.chartLibrary || widget.chartLibrary,
									// Include chartType in config for WidgetViewRenderer
									chartType: draftedChartType
								},
								facts: widget.drafted.facts || widget.facts
							},
							position: widget.drafted.position,
							placement: widget.drafted.placement || "components",
							created: widget.created,
							modified: widget.modified,
							modifierId: widget.modifierId,
							creatorId: widget.creatorId,
							organizationId: widget.organizationId,
							isExternal: widget.drafted.isExternal,
							// Preserve editorSource from widget or drafted
							editorSource: widget.drafted.editorSource || widget.editorSource,
							// Preserve chartLibrary from widget or drafted
							chartLibrary: widget.drafted.chartLibrary || widget.chartLibrary,
							chartType: draftedChartType
						};
						// Also preserve editorSource in position if it exists
						if (widgetObj.position && (widget.drafted.position?.editorSource || widget.position?.editorSource)) {
							widgetObj.position = {
								...widgetObj.position,
								editorSource: widget.drafted.position?.editorSource || widget.position?.editorSource
							};
						}
						response.editorWidgets.push(widgetObj);
					}
				} else {
					response.editorWidgets.push(widgetObj);
				}
			});
			perfLog("sipCrud.getDashboardWidgets", {
				dashboardId,
				networkMs: __networkMs,
				totalMs: Date.now() - __start,
				widgetCount: widgets.length
			});
			return response;
		});
};

export const createStarterPackDashboardContainer = async organizationId => {
	try {
		const response = await axios.post("/api/dashboardContainers/createFirstContainerAndDashboard", {
			organizationId: parseInt(organizationId)
		});
		return response;
	} catch (error) {
		console.error("Error creating starter pack dashboard container:", error);
		return Promise.reject(error);
	}
};

export const getWidgetAPI = widgetId => {
	return axios
		.get(`/api/widgets/${widgetId}`)
		.then(respose => respose.data)
		.then(widgetConfig => {
			const result = { ...widgetConfig };
			delete result._id;
			result.data = result.data ? result.data : [];
			result.props = result.props ? result.props : {};
			result.config = result.config ? result.config : {};
			return result;
		});
};

export const getDashboard = dashboardId => {
	return axios.get(`/api/dashboards/${dashboardId}`).then(response => response.data);
};

// Note :- arg here is an array
/**
 * Returns the organizationIds of every dashboard the current user can actually reach.
 * /api/dashboards is access filtered server side (heimdall dashboard context), so this
 * is the authoritative list of organizations where the user has dashboard access.
 * Resolves to null when the lookup fails, so callers can fail open instead of
 * hiding everything on a transient error.
 */
export const getAccessibleDashboardOrgIds = () => {
	return axios
		.get("/api/dashboards/", {
			params: {
				filter: {
					fields: ["id", "organizationId"]
				}
			}
		})
		.then(response => {
			const orgIds = (response.data || [])
				.map(dashboard => Number(dashboard.organizationId))
				.filter(orgId => !Number.isNaN(orgId));
			return [...new Set(orgIds)];
		})
		.catch(error => {
			console.error("Error fetching accessible dashboard organizations:", error);
			return null;
		});
};

export const getDashboardFromDashboardContainerIds = arrOfDashboardContainerIDs => {
	return axios
		.get(`/api/dashboards/`, {
			params: {
				filter: {
					where: {
						dashboardContainerId: {
							inq: arrOfDashboardContainerIDs
						}
					}
				}
			}
		})
		.then(response => response.data);
};

export const addDashboard = params => {
	return axios.post("/api/dashboards/", params);
};

export const publishDashboard = dashboardId => {
	return axios.post(`/api/dashboards/publish`, { dashboardId }).then(response => response.data);
};

// Tracks every in-flight updateWidgetAPI request (drag/resize/config saves fired
// fire-and-forget from the editors) so publishDashboard callers can await
// flushPendingWidgetSaves() first. Without this, a position/config PUT still in
// flight when the user hits Publish loses the race: publishDashboard only tells
// the backend to promote whatever `drafted` data it already has, so an
// unfinished save is silently published as if it never happened, then reappears
// stale after the next refresh.
const pendingWidgetSaves = new Set();

export const flushPendingWidgetSaves = () => Promise.allSettled(Array.from(pendingWidgetSaves));

export const updateWidgetAPI = (widgetId, widgetObject, reduxWidgetObj) => {
	// Extract chartLibrary, chartType, and editorSource to save at root level
	const { chartLibrary, chartType, editorSource, ...draftedProps } = widgetObject || {};
	
	const updatePayload = {
		drafted: {
			...reduxWidgetObj.configuration,
			position: reduxWidgetObj.position,
			placement: reduxWidgetObj.placement,
			name: reduxWidgetObj.configuration.config.name,
			isExternal: reduxWidgetObj.isExternal,
			...draftedProps,
			// Include editorSource in drafted data
			...(editorSource !== undefined && { editorSource }),
			active: true
		}
	};
	
	// Save chartLibrary, chartType, and editorSource at widget root level
	// This ensures they persist after publishing (backend publish copies root-level fields)
	if (chartLibrary !== undefined) {
		updatePayload.chartLibrary = chartLibrary;
	}
	if (chartType !== undefined) {
		updatePayload.chartType = chartType;
	}
	if (editorSource !== undefined) {
		updatePayload.editorSource = editorSource;
	}
	
	const request = axios.put(`/api/widgets/${widgetId}`, updatePayload).then(response => response.data);
	pendingWidgetSaves.add(request);
	const untrack = () => pendingWidgetSaves.delete(request);
	request.then(untrack, untrack);
	return request;
};

export const updateDashboardAPI = (dashboardId, dataObject) => {
	return axios.put(`/api/dashboards/${dashboardId}`, dataObject).then(response => response.data);
};

export const deleteWidgetAPI = (widgetId, reduxWidgetObj) => {
	// Soft-delete the widget by marking inactive/deleted at root and drafted level.
	// Include chartLibrary/chartType/editorSource at root so the backend receives complete context.
	const widgetObject = {
		active: false,
		deleted: true,
		chartLibrary: reduxWidgetObj.chartLibrary,
		chartType: reduxWidgetObj.chartType,
		editorSource: reduxWidgetObj.editorSource,
		drafted: {
			...reduxWidgetObj.configuration,
			position: reduxWidgetObj.position,
			placement: reduxWidgetObj.placement,
			name: reduxWidgetObj.configuration?.config?.name,
			active: false,
			isExternal: reduxWidgetObj.isExternal
		}
	};

	return axios.put(`/api/widgets/${widgetId}`, widgetObject).then(response => response.data);
};

export const getDashboardFilters = dashboardId => {
	const filtersState = [];
	let filterComponents = [];
	return axios
		.get(`/api/dashboard/${dashboardId}`)
		.then(response => response.data)
		.then(dashboardInstance => {
			const filters = dashboardInstance.filters || [];
			filterComponents = dashboardInstance.components.filter(
				component => component.componentType === "filter"
			);
			const filterIds = filters.map(val => {
				if (typeof val === "string") {
					return val;
				} else {
					return val._id;
				}
			});
			const filterComponentIds = filterComponents.map(val => val._id);
			const filtersPromise = [...filterIds, ...filterComponentIds].map(val => {
				return axios.get(`/api/filters/${val}`).then(response => response.data);
			});
			return Promise.all(filtersPromise);
		})
		.then(filtersData => {
			filtersData.forEach(filterData => {
				const { placement } = filterData;
				let position = {};
				if (placement === "LOCAL") {
					const targetFilter = filterComponents.find(val => val._id === filterData.id);
					position = targetFilter;
					delete position._id;
				}
				filtersState.push({
					configuration: {
						data: filterData.data || [],
						name: filterData.name || "",
						type: filterData.type || "",
						refWidgetId: filterData.refWidgetId || "SELECT_FILTER"
					},
					placement: filterData.placement || "GLOBAL",
					position,
					id: filterData.id
				});
			});
			const filtersId = filtersData.map(val => val.id);
			const dashboardFiltersPromise = filtersId.map(filterId => {
				return axios
					.get(`/api/dashboardFilters/findOne`, {
						params: {
							filter: {
								where: {
									filterId: filterId
								}
							}
						}
					})
					.then(response => response.data);
			});
			return Promise.all(dashboardFiltersPromise);
		})
		.then(dashboardFiltersData => {
			filtersState.forEach((filterState, index) => {
				filterState.dashboardFilters = {
					widgets: dashboardFiltersData[index].widgets
				};
				filterState.dashboardFilterId = dashboardFiltersData[index].id;
			});
			return Promise.resolve(filtersState);
		});
};

export const createNewFilterAPI = payload => {
	return axios.post(`/api/filters/createAndAddToDashboard`, payload).then(response => response.data);
};

export const updateFilterApi = (filterId, payload) => {
	return axios.put(`/api/filters/${filterId}`, payload).then(response => response.data);
};

export const createDashboardFilter = payload => {
	return axios.post(`/api/dashboardFilters`, payload).then(response => response.data);
};

export const getQueryKeywords = qid => {
	return axios
		.get(`/api/queries/execute/${qid}`)
		.then(response => response.data)
		.then(queryData => {
			const { data } = queryData;
			return data.length ? Object.keys(data[0]) : [];
		});
};

export const deleteFilterAPI = filterId => {
	return axios.delete(`/api/filters/${filterId}`).then(response => response.data);
};

export const deleteDashboardFilter = dashboardFilterId => {
	return axios.delete(`/api/dashboardFilters/${dashboardFilterId}`).then(response => response.data);
};

export const updateDashboardFilterAPI = (dashboardFilterId, payload) => {
	return axios.put(`/api/dashboardFilters/${dashboardFilterId}`, payload).then(response => response.data);
};

export const getOrganizationDetail = orgId => {
	return axios
		.post("aggregate/table", {
			table: "organization",
			query: [
				{
					where: {
						organizationId: parseInt(orgId, 10)
					}
				}
			]
		})
		.then(response => response.data);
};


const baseDelay = 3000;
const maxDelay = 7000;
const maxRetries = 3;
const REQUEST_TIMEOUT = 20000;

const wait = retryCount => {
	// Exponential backoff with jitter. This MUST always resolve, otherwise a
	// caller awaiting it hangs forever (the old guard skipped setTimeout once
	// retryCount > maxRetries, leaving the widget spinner spinning indefinitely).
	const backoff = Math.min(maxDelay, 2 ** retryCount * 1000);
	const delay = Math.min(maxDelay, baseDelay + Math.random() * (backoff - baseDelay));
	return new Promise(resolve => setTimeout(resolve, delay));
};

// Retry only on transient failures (network error, timeout, or 5xx). A
// deterministic 4xx (bad request / unauthorized / not found) will never succeed
// on retry, so retrying it just burns ~21s of backoff per widget.
const isTransientError = error => {
	if (!error || !error.response) {
		return true; // network error / no response
	}
	if (error.code === "ECONNABORTED") {
		return true; // timeout
	}
	return error.response.status >= 500;
};

// coerce:false keeps type information in the hash, so a filter value of 1
// (number) and "1" (string) produce DIFFERENT keys. With coercion on, they
// would collide and one widget could be served another's data.
const requestHasher = Hasher({ sort: true, coerce: false });

// In-flight request de-duplication. On dashboard open many widgets often request
// the SAME indicator data at once; collapse those into a single network call.
// Each subscriber gets an independent deep clone so a downstream transform can't
// mutate another widget's data. The entry is removed once the shared request
// settles, so later requests fetch fresh data (no staleness introduced).
const inFlightRequests = new Map(); // key -> { promise, internalSource, subscribers }

const cloneData = data => {
	if (data === null || data === undefined) {
		return data;
	}
	// Prefer structuredClone: it preserves NaN/Infinity and undefined-in-arrays,
	// which a JSON round-trip would turn into null. Fall back to a JSON clone
	// where structuredClone is unavailable.
	try {
		if (typeof structuredClone === "function") {
			return structuredClone(data);
		}
	} catch (e) {
		// fall through to JSON clone
	}
	try {
		return JSON.parse(JSON.stringify(data));
	} catch (e) {
		// API responses are JSON, so this should not happen; share as a fallback.
		return data;
	}
};

// Performs the actual request through the shared concurrency limiter, retrying
// only transient failures. Always settles (never hangs, never rejects).
const executeIndicatorRequest = (methodUrl, indicatorObj, cancelToken, retries = 0) => {
	const __enqueuedAt = Date.now();
	let __dequeuedAt = null;
	return indicatorRequestLimiter(() => {
		// Time spent waiting behind the concurrency cap (limiter.js), separate
		// from actual network time below.
		__dequeuedAt = Date.now();
		// Skipped if the (internal) request was cancelled while queued.
		if (cancelToken && cancelToken.reason) {
			return null;
		}
		return axios.post(
			methodUrl,
			{
				...indicatorObj
			},
			{
				cancelToken: cancelToken,
				timeout: REQUEST_TIMEOUT
			}
		);
	})
		.then(response => {
			const __dequeueTs = __dequeuedAt || Date.now();
			perfLog("sipCrud.executeIndicatorRequest", {
				methodUrl,
				retries,
				queueMs: __dequeueTs - __enqueuedAt,
				networkMs: Date.now() - __dequeueTs
			});
			return response ? response.data : null;
		})
		.catch(error => {
			if (axios.isCancel(error)) {
				return null;
			}
			if (isTransientError(error) && retries < maxRetries) {
				return wait(retries).then(() => {
					if (cancelToken && cancelToken.reason) {
						return null;
					}
					return executeIndicatorRequest(methodUrl, indicatorObj, cancelToken, retries + 1);
				});
			}
			const __dequeueTs = __dequeuedAt || Date.now();
			perfLog("sipCrud.executeIndicatorRequest", {
				methodUrl,
				retries,
				queueMs: __dequeueTs - __enqueuedAt,
				networkMs: Date.now() - __dequeueTs,
				error: true
			});
			console.error("Indicator data request failed", error);
			return null;
		});
};

// ---- Request micro-batching ----
// A dashboard open fires many /indicator/get* requests within a few ms of each
// other. Instead of paying full per-request overhead (auth, project-scope,
// connection) for each, collect them for a short window and send ONE
// /indicator/getBatch call; the server resolves them concurrently and returns
// results keyed per request. If the batch endpoint is unavailable (older
// backend, 404/405) batching disables itself for the session and every request
// falls back to the individual path - so this deploys safely ahead of the
// backend.
const BATCH_FLUSH_MS = 25;
const BATCH_MAX_ITEMS = 15;
// A batch aggregates many queries, so it gets a larger budget than the 20s
// individual-request timeout.
const BATCH_REQUEST_TIMEOUT = 60000;
const BATCHABLE_TYPES = {
	"/indicator/getPoint": "point",
	"/indicator/getSeries": "series",
	"/indicator/getTable": "table"
};

let batchingSupported = apiUrlConfig?.apiConfig?.disableIndicatorBatch !== true;
let pendingBatch = [];
let batchFlushTimer = null;
let batchKeySeq = 0;

// Fall back to the individual path (which carries transient-retry + timeout +
// cancellation). Each item keeps its own cancel token so replays for a
// navigated-away widget can be skipped instead of running to completion.
const replayIndividually = items => {
	items.forEach(item => {
		executeIndicatorRequest(item.methodUrl, item.indicatorObj, item.cancelToken || null).then(item.resolve);
	});
};

const flushBatch = () => {
	batchFlushTimer = null;
	if (!pendingBatch.length) {
		return;
	}
	const items = pendingBatch;
	pendingBatch = [];
	const __enqueuedAt = Date.now();
	let __dequeuedAt = null;
	indicatorRequestLimiter(() => {
		__dequeuedAt = Date.now();
		return axios.post(
			"/indicator/getBatch",
			{
				requests: items.map(item => ({
					key: item.key,
					type: BATCHABLE_TYPES[item.methodUrl],
					params: { ...item.indicatorObj }
				}))
			},
			{ timeout: BATCH_REQUEST_TIMEOUT }
		);
	})
		.then(response => {
			const __dequeueTs = __dequeuedAt || Date.now();
			const __timing = { queueMs: __dequeueTs - __enqueuedAt, networkMs: Date.now() - __dequeueTs };
			const results = response && response.data && response.data.results;
			if (!Array.isArray(results)) {
				// 200 but not the batch contract (SPA fallback HTML / wrong shape
				// / proxy page) => endpoint isn't really there. Stop batching for
				// the session and serve everything the individual way.
				perfLog("sipCrud.flushBatch", { batchSize: items.length, ...__timing, outcome: "invalidShape-replayAll" });
				batchingSupported = false;
				replayIndividually(items);
				return;
			}
			const byKey = new Map(results.map(r => [r.key, r]));
			let succeeded = 0;
			let replayed = 0;
			items.forEach(item => {
				const result = byKey.get(item.key);
				if (result && result.success) {
					// result.data is the exact same envelope the individual
					// endpoint returns ({response, target, targetResult, ...}).
					succeeded += 1;
					item.resolve(result.data);
				} else {
					// Item failed inside the batch, or the server returned no
					// entry for this key -> replay individually so it still gets
					// the normal transient-retry semantics before giving up.
					replayed += 1;
					replayIndividually([item]);
				}
			});
			perfLog("sipCrud.flushBatch", { batchSize: items.length, ...__timing, succeeded, replayed });
		})
		.catch(error => {
			const __dequeueTs = __dequeuedAt || Date.now();
			perfLog("sipCrud.flushBatch", {
				batchSize: items.length,
				queueMs: __dequeueTs - __enqueuedAt,
				networkMs: Date.now() - __dequeueTs,
				outcome: "error-replayAll"
			});
			if (!axios.isCancel(error) && error && error.response) {
				const status = error.response.status;
				// A 4xx / 501 for the batch route is a contract/deployment
				// problem that won't fix itself -> stop batching for the session.
				// 5xx / network errors may be transient, so keep batching on and
				// just replay this burst individually.
				if ((status >= 400 && status < 500) || status === 501) {
					batchingSupported = false;
				}
			}
			replayIndividually(items);
		});
};

const enqueueInBatch = (methodUrl, indicatorObj, cancelToken) =>
	new Promise(resolve => {
		pendingBatch.push({ key: "b" + ++batchKeySeq, methodUrl, indicatorObj, cancelToken, resolve });
		if (pendingBatch.length >= BATCH_MAX_ITEMS) {
			if (batchFlushTimer) {
				clearTimeout(batchFlushTimer);
				batchFlushTimer = null;
			}
			flushBatch();
		} else if (!batchFlushTimer) {
			batchFlushTimer = setTimeout(flushBatch, BATCH_FLUSH_MS);
		}
	});

export const getIndicatorData = (indicatorObj, method, source, isExternal, organizationId) => {
	let methodUrl = "/indicator/";
	if (indicatorObj.indicatorId) {
		switch (method) {
			case "point":
				methodUrl = methodUrl + "getPoint";
				break;
			case "series":
				methodUrl = methodUrl + "getSeries";
				break;
			case "table":
				methodUrl = methodUrl + "getTable";
				break;
		}
	} else if (indicatorObj.queryId) {
		methodUrl = "/api/facts/filter";
	} else {
		return Promise.resolve(indicatorObj);
	}

	if (isExternal) {
		indicatorObj.organizationId = parseInt(organizationId, 10) || 0;
		indicatorObj["thirdParty"] = {
			exploreId: indicatorObj.queryId
		};
		delete indicatorObj.queryId;
	}

	// Key by the full normalized request, so any difference (including filter
	// selections, which live in indicatorObj) produces a distinct key.
	const requestKey = requestHasher.hash({ methodUrl, indicatorObj, isExternal, organizationId });

	let entry = inFlightRequests.get(requestKey);
	if (!entry) {
		const internalSource = axios.CancelToken.source();
		// Route standard indicator requests through the micro-batcher (one
		// /indicator/getBatch call for the whole burst); external/facts
		// requests keep the individual path.
		const useBatch = batchingSupported && BATCHABLE_TYPES[methodUrl] && !isExternal;
		const basePromise = useBatch
			? enqueueInBatch(methodUrl, { ...indicatorObj }, internalSource.token)
			: executeIndicatorRequest(methodUrl, indicatorObj, internalSource.token);
		const promise = basePromise.then(data => {
			inFlightRequests.delete(requestKey);
			return data;
		});
		entry = { promise, internalSource, subscribers: 0 };
		inFlightRequests.set(requestKey, entry);
	}

	entry.subscribers += 1;

	return new Promise(resolve => {
		let settled = false;
		const finish = value => {
			if (!settled) {
				settled = true;
				resolve(value);
			}
		};

		// The shared request never rejects (it resolves null on failure).
		entry.promise.then(data => finish(cloneData(data)));

		// If THIS caller cancels (its widget unmounted / re-ran the effect),
		// resolve it with null. Abort the shared request only once every
		// subscriber has cancelled, so one widget can't kill another's fetch.
		if (source && source.token && source.token.promise) {
			source.token.promise.then(() => {
				entry.subscribers -= 1;
				if (entry.subscribers <= 0 && entry.internalSource) {
					entry.internalSource.cancel("All indicator subscribers cancelled");
				}
				finish(null);
			});
		}
	});
};
export const duplicateWidget = (widgetId, placement, placementData) => {
	const toNumber = value => {
		const n = Number(value);
		return Number.isFinite(n) ? n : 0;
	};

	// Cap span/height to avoid backend validation failures (e.g., grid width limits)
	const clamp = (value, max = 12, min = 1) => {
		const n = toNumber(value);
		if (!n || n < min) return min;
		return n > max ? max : n;
	};

	// Ensure placement and placementData are always present with defaults
	const normalizedPlacement = placement || "components";
	const normalizedPlacementData =
		placementData && typeof placementData === "object"
			? {
					x: toNumber(placementData.x),
					y: toNumber(placementData.y),
					// cap span/height to 12 (common grid width/height) to prevent 502s
					span: clamp(placementData.span, 12, 1) || 6,
					height: clamp(placementData.height, 12, 1) || 6
			  }
			: { x: 0, y: 0, span: 6, height: 6 };

	const postBody = {
		widgetId,
		placement: normalizedPlacement,
		placementData: normalizedPlacementData
	};

	// Single path: use duplicateWidget endpoint for both old and new editors
	return axios.post("api/widgets/duplicateWidget", postBody).then(response => response.data);
};

export const moveWidget = (widgetId, dashboardId) => {
	var postBody = { widgetId, dashboardId };
	return axios.post("/api/widgets/moveWidget", postBody).then(response => response.data);
};

export const duplicateDashboard = (id, extraProps) => {
	let body = {
		dashboardId: id,
		...extraProps
	};
	return axios.post("api/dashboards/duplicate", body).then(response => response);
};

export const getDashboardUsers = orgId => {
	return axios
		.get("api/dashboardUsers", {
			params: {
				filter: { where: { organizationId: orgId } }
			}
		})
		.then(respose => respose.data);
};

export const whoAmI = (userId, dashboardId) => {
	const __start = Date.now();
	return axios
		.get("api/dashboardUsers/findOne", {
			params: {
				filter: { where: { dashboardId: dashboardId, userId: userId } }
			}
		})
		.then(respose => {
			perfLog("sipCrud.whoAmI", { dashboardId, ms: Date.now() - __start });
			return respose.data;
		})
		.catch(err => {
			perfLog("sipCrud.whoAmI", { dashboardId, ms: Date.now() - __start, error: true });
		});
};

export const addDashboardUsers = (orgId, payload, tagsAndTagElements) => {
	return axios.post("api/dashboardUsers/createInBulk", {
		organizationId: orgId,
		payload,
		tagsAndTagElements
	});
};

export const deleteDashboardUser = (orgId, userId) => {
	return axios.post("api/dashboardUsers/deleteUsers", {
		organizationId: orgId,
		userId: userId
	});
};

export const removeDashboardAccess = dashboardUserId => {
	return axios.delete("/api/dashboardUsers/" + dashboardUserId);
};

export const getDashboardFromSource = sourceId => {
	return axios
		.get(`/api/dashboards/`, {
			params: {
				filter: {
					where: {
						sourceId: sourceId + "",
						active: true
					}
				}
			}
		})
		.then(response => response.data);
};

export const downloadDashboard = params => {
	return axios.post(`/api/dashboards/downloadDashboard`, params);
};

export const getPublicLink = dashboardId => {
	return axios
		.get("api/PublicDashboards/", {
			params: {
				filter: { where: { dashboardId } }
			}
		})
		.then(respose => respose.data)
		.catch(err => {});
};

export const generatePublicLink = params => {
	return axios
		.post("api/PublicDashboards/generatePublicLink", params)
		.then(response => response)
		.catch(err => {
			console.log("generatePublicLink", err);
		});
};

export const storePublicLink = params => {
	return axios
		.post("api/PublicDashboards/", params)
		.then(response => response)
		.catch(err => {
			console.log("storePublicLink", err);
		});
};

export const getProgram = orgId => {
	return axios
		.get(`${apiUrlConfig.apiConfig.p3fyUrl}/programs`, {
			params: {
				filter: { where: { code: { regexp: orgId } } }
			}
		})
		.then(respose => respose.data)
		.catch(err => {});
};
